// src/admin/AdminReliability.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../firebase";

const dayFmt = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function fmtDate(v) {
  try {
    if (!v) return "—";
    // Firestore Timestamp support
    if (typeof v.toDate === "function") return dayFmt.format(v.toDate());
    const d = new Date(v);
    if (!Number.isFinite(d.getTime())) return "—";
    return dayFmt.format(d);
  } catch {
    return "—";
  }
}

function fmtAgo(v) {
  try {
    let ms;
    if (!v) return "";
    if (typeof v.toMillis === "function") ms = v.toMillis();
    else ms = new Date(v).getTime();
    if (!Number.isFinite(ms)) return "";
    const diff = Date.now() - ms;
    if (diff < 0) return "";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days <= 0) return "today";
    if (days === 1) return "1 day ago";
    if (days < 30) return `${days} days ago`;
    const months = Math.floor(days / 30);
    if (months === 1) return "1 month ago";
    if (months < 12) return `${months} months ago`;
    const years = Math.floor(months / 12);
    return years === 1 ? "1 year ago" : `${years} years ago`;
  } catch {
    return "";
  }
}

export default function AdminReliability() {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const qRef = query(
      collection(db, "users"),
      // If host metrics live on all users, we just sort; if you have an isHost
      // flag you can add orderBy/isHost filtering later.
      orderBy("hostCancelCount", "desc"),
      limit(200)
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const out = [];
        snap.forEach((d) => out.push({ id: d.id, ...(d.data() || {}) }));
        setHosts(out);
        setLoading(false);
      },
      (err) => {
        console.error("AdminReliability snapshot error", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const enriched = useMemo(() => {
    return hosts
      .map((h) => {
        const total = Number(h.hostCancelCount ?? h.host_cancel_count ?? 0);
        const late = Number(h.hostLateCancelCount ?? h.host_cancel_late_count ?? 0);
        const lastAt =
          h.hostCancelLastAt ??
          h.host_cancel_last_at ??
          h.hostCancelLastDate ??
          null;
        const lastReason =
          h.hostCancelLastReason ??
          h.host_cancel_last_reason ??
          h.hostCancelLastNote ??
          "";

        const isHost =
          h.isHost === true ||
          h.host === true ||
          h.role === "host" ||
          h.roles?.includes?.("host");

        return {
          ...h,
          _totalCancels: total,
          _lateCancels: late,
          _lastAt: lastAt,
          _lastReason: lastReason,
          _isHost: isHost,
        };
      })
      // Show real hosts first and only those who have at least 1 cancel
      .filter((h) => h._isHost && h._totalCancels > 0)
      .sort((a, b) => {
        if (b._lateCancels !== a._lateCancels) {
          return b._lateCancels - a._lateCancels;
        }
        return b._totalCancels - a._totalCancels;
      });
  }, [hosts]);

  const stats = useMemo(() => {
    const totalHosts = enriched.length;
    const anyLate = enriched.filter((h) => h._lateCancels > 0).length;
    const heavy =
      enriched.filter((h) => h._totalCancels >= 3 || h._lateCancels >= 1).length;
    return { totalHosts, anyLate, heavy };
  }, [enriched]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Reliability &amp; cancellations
          </h1>
          <p className="mt-1 max-w-xl text-sm text-neutral-600">
            Monitor host-initiated cancellations, spot repeat offenders, and
            protect guest trust. Hosts with frequent or last-minute cancellations
            are surfaced here.
          </p>
        </div>
      </header>

      {/* Summary cards */}
      <section className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
            Hosts with cancellations
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-semibold">{stats.totalHosts}</span>
            <span className="text-xs text-neutral-500">in last sync</span>
          </div>
          <p className="mt-1 text-xs text-neutral-500">
            Any host with at least one recorded host-side cancellation.
          </p>
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-amber-700">
            Last-minute cancellations
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-amber-900">
              {stats.anyLate}
            </span>
          </div>
          <p className="mt-1 text-xs text-amber-800">
            Hosts with at least one late host cancellation (e.g. within 48 hours
            of check-in).
          </p>
        </div>

        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-red-700">
            Watchlist (heavy offenders)
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-red-900">
              {stats.heavy}
            </span>
          </div>
          <p className="mt-1 text-xs text-red-800">
            Hosts with 3+ host cancellations total or any late cancellation.
          </p>
        </div>
      </section>

      {/* Table */}
      <section className="overflow-x-auto rounded-2xl border border-neutral-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
            <tr>
              <th className="px-4 py-2">Host</th>
              <th className="px-4 py-2">Totals</th>
              <th className="px-4 py-2">Last cancel</th>
              <th className="px-4 py-2">Last reason</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && enriched.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-neutral-500">
                  Loading host reliability…
                </td>
              </tr>
            )}

            {!loading && enriched.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-neutral-500">
                  No host cancellations recorded yet.
                </td>
              </tr>
            )}

            {enriched.map((h) => {
              const name =
                h.displayName ||
                h.name ||
                h.fullName ||
                h.profileName ||
                "Host";
              const email = h.email || h.loginEmail || "—";
              const label =
                h._lateCancels > 0 || h._totalCancels >= 3
                  ? "Watchlist"
                  : h._totalCancels >= 1
                  ? "Elevated"
                  : "";
              const labelCls =
                h._lateCancels > 0 || h._totalCancels >= 3
                  ? "bg-red-100 text-red-700 border-red-200"
                  : "bg-amber-100 text-amber-700 border-amber-200";

              const lastDateLabel = fmtDate(h._lastAt);
              const agoLabel = fmtAgo(h._lastAt);
              const reason =
                (h._lastReason || "").trim().slice(0, 160) ||
                "No reason captured.";

              return (
                <tr key={h.id} className="border-t border-neutral-100 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium text-neutral-900">{name}</div>
                    <div className="text-xs text-neutral-500">{email}</div>
                    {label && (
                      <span
                        className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[11px] ${labelCls}`}
                      >
                        {label}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <span className="font-semibold">{h._totalCancels}</span>{" "}
                      total
                    </div>
                    <div className="text-xs text-neutral-500">
                      {h._lateCancels} late / short-notice
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">{lastDateLabel}</div>
                    <div className="text-xs text-neutral-500">{agoLabel}</div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="max-w-xs text-xs text-neutral-700">
                      {reason}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={`/admin/users?uid=${encodeURIComponent(h.id)}`}
                        className="rounded-full border border-neutral-300 px-3 py-1 text-xs hover:bg-neutral-50"
                      >
                        View user
                      </Link>
                      {/* Placeholder for future controls (e.g. soft-block) */}
                      <button
                        type="button"
                        disabled
                        className="rounded-full border border-neutral-200 px-3 py-1 text-xs text-neutral-400"
                        title="Coming soon"
                      >
                        Adjust hosting status
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <p className="text-xs text-neutral-500">
        Note: this view is read-only. Actual penalties (like pausing a host or
        requiring explanations) should be applied via your{" "}
        <Link to="/admin/users" className="underline">
          Users
        </Link>{" "}
        tooling or a future “Host trust” workflow.
      </p>
    </div>
  );
}
